import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import {
    DataGrid,
    GridColDef,
    GridToolbar,
    GridValueFormatterParams
} from "@mui/x-data-grid";
import { Box, Button, Dialog, DialogContent, DialogTitle, FormControl, FormHelperText, MenuItem, TextField, Typography } from "@mui/material";
import { branch, state, transaction } from "../common/type";
import { getBranches, getNextStates, getTransactions } from "../services/get.service";
import { useForm } from "react-hook-form";
import ClipLoader from "react-spinners/ClipLoader";
import { toast } from "react-toastify";
import { changeState } from "../services/post.service";

interface ITransactionFilterFields {
    branchId: number;
    start: string;
    end: string;
    year: number;
    cardorPhone: string;
}

const Transactions = () => {
    var date = new Date().toISOString().substring(0, 10)
    const [open, setOpen] = useState(false)
    const columns: GridColDef[] = [
        { field: 'patientName', headerName: "Full Name", width: 170 },
        { field: 'branchName', headerName: "Branch Name", width: 170 },
        { field: 'treatmentName', headerName: "Treatment Name", width: 170 },
        {
            field: 'amount', headerName: "Amount", width: 170, type: "number", valueFormatter: (val: GridValueFormatterParams<number>) => {
                return val.value.toLocaleString(undefined, {
                    maximumFractionDigits: 2
                })
            }
        },
        { field: 'lastState', headerName: "Last State", width: 170 },
        { field: 'type', headerName: "Type", width: 170 },
        {
            field: 'createdDate', headerName: "Created Date", width: 170, type: "datetime", valueFormatter: (val: GridValueFormatterParams<Date>) => {
                var date = new Date(val.value)
                return date.toLocaleDateString("en", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                })
            }
        },
        { field: 'purpose', headerName: "Purpose", width: 170 },
        { field: 'description', headerName: "Description", width: 170 }
    ]
    const [braches, setBranches] = useState<Array<branch>>([])
    const [transactions, setTransactions] = useState<Array<transaction>>([])
    const [states, setStates] = useState<state[]>([])
    const [loading, setLoading] = useState(false)
    const [loading2, setLoading2] = useState(false)
    const [transactionId, setTransactionId] = useState<number>(0)

    useEffect(() => {
        getBranches().then((res) => setBranches(res.data)).catch((err) => {
            console.log(err);
        })
    }, [])

    const { register, handleSubmit, watch } = useForm<ITransactionFilterFields>()

    const OnSubmit = (data: any) => {
        setLoading(true)
        getTransactions(data)
            .then((res) => {
                setLoading(false)
                setTransactions(res.data)
            })
            .catch((err) => {
                setLoading(false)
                console.log(err);
            })
    }

    const fetchStates = (fromState: any) => {
        var from = 0
        transactions.filter((tr) => {
            if (tr.id === fromState) {
                from = tr.lastStateReference
                setTransactionId(tr.id)
            }

            return tr;
        })

        setLoading2(true)
        getNextStates(from)
            .then((res) => {
                setLoading2(false)
                setStates(res.data)
            })
            .catch((err) => {
                setLoading2(false)
                console.log(err);
            })
    }

    const changeStateFunc = (to: number) => {
        const load = toast.loading("Please wait...", {
            theme: "colored",
            type: "info",
            closeButton: true,
        })

        changeState({
            toState: to,
            reference: transactionId
        })
            .then((res) => {
                toast.update(load, {
                    render: res.data,
                    type: "success",
                    theme: "colored",
                    autoClose: 2500,
                    isLoading: false,
                    closeOnClick: true,
                    hideProgressBar: false,
                });
            })
            .catch((err) => {
                toast.update(load, {
                    render: err?.response?.data?.detail,
                    theme: "colored",
                    autoClose: 5000,
                    type: "error",
                    isLoading: false,
                    closeOnClick: true,
                    hideProgressBar: false,
                });
                console.log(err);
            })
    }

    return <>
        <Helmet>
            <title>Transactions</title>
        </Helmet>
        <div className="dark:text-black w-full">
            <div className="lg:m-12 m-4">
                <div className="bg-amber50 shadow-md rounded w-full px-3 lg:px-20 py-10 pt-5">
                    <Typography variant="h4" fontSize={20} className="py-3 underline">Filters</Typography>
                    <form className="lg:grid lg:grid-cols-4 lg:gap-3 grid grid-cols-2 gap-2" onSubmit={handleSubmit(OnSubmit)}>
                        <TextField size="small" select label="Organizations" {...register("branchId")}>
                            <MenuItem value="">Select Organization</MenuItem>
                            {
                                braches.map((branch) => (
                                    <MenuItem value={branch.id}>{branch.name}</MenuItem>
                                ))
                            }
                        </TextField>
                        <TextField
                            error={(watch("start") !== "" && watch("end") !== "") && new Date(watch("start")) > new Date(watch("end")) ? true : false}
                            size="small" type="date" label="Start" {...register("start")} defaultValue={date} />
                        <FormControl>
                            <TextField
                                error={(watch("start") !== "" && watch("end") !== "") && new Date(watch("start")) > new Date(watch("end")) ? true : false}
                                size="small" type="date" label="End" {...register("end")} defaultValue={date} />
                            <FormHelperText className="text-red-700 text-sm"
                                error={(watch("start") !== "" && watch("end") !== "") && new Date(watch("start")) > new Date(watch("end")) ? true : false}
                            >
                                {(watch("start") !== "" && watch("end") !== "") && new Date(watch("start")) > new Date(watch("end"))
                                    ? "Start must be less or equal to End!"
                                    : ""
                                }
                            </FormHelperText>
                        </FormControl>
                        <TextField {...register("year")} size="small" placeholder="Year" inputProps={{ min: 2024 }} type="number" label="Year" required />
                        <TextField {...register("cardorPhone")} size="small" placeholder="Card or Phone" type="text" label="Card or Phone" />
                        <Button type="submit" className="lg:col-span-3" variant="contained">Fetch</Button>
                    </form>
                    <Typography variant="h4" fontSize={32} className="pt-6">Transactions</Typography>
                    <Box sx={{ height: 520, width: "100%" }}>
                        {loading ?
                            <ClipLoader size={50} />
                            :
                            <DataGrid onRowClick={(e) => {
                                fetchStates(e.id)
                                setOpen(true)
                            }}
                                rows={transactions} columns={columns} slots={{ toolbar: GridToolbar }} />
                        }
                    </Box>
                </div>
            </div>
        </div>
        <Dialog open={open} onClose={() => setOpen(false)}>
            <DialogTitle>Change State</DialogTitle>
            <DialogContent>
                {loading2
                    ?
                    <ClipLoader />
                    :
                    <div className="grid grid-cols-2 gap-2">
                        {states.length > 0 ?
                            states.map((state) => (
                                <Button variant="contained" onClick={() => {
                                    setOpen(false)
                                    var ans = window.confirm('Are you sure?')
                                    if (ans)
                                        changeStateFunc(state.id)
                                }}>To {state.name}</Button>
                            ))
                            :
                            <>No further state</>
                        }
                    </div>
                }
            </DialogContent>
        </Dialog>
    </>;
};

export default Transactions;
